'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Water Filters for Microplastics: 6 Brands Tested and Ranked"
      author="Elise Maren"
      date="May 19, 2026"
      readTime="14 min read"
      heroImage="/blog/heroes/microplastics-water-filters-compared.webp"
      heroImageAlt="A countertop water filter pitcher beside a glass of clean water and a filter cartridge"
    >
      <BlogLastUpdated iso="2026-06-11" formatted="June 11, 2026" />

      <BlogTLDR>
        Head-to-head: <strong>AquaTru countertop RO</strong> wins for raw
        microplastic removal (99%+ down to nanoparticles) without plumbing.
        <strong> Clearly Filtered pitcher</strong> wins for budget-conscious
        NSF 401-certified microplastic claim. <strong>Big Berkey</strong> wins
        for off-grid / gravity-fed reliability. <strong>APEC ROES-50</strong>
        wins for under-sink whole-kitchen RO. <strong>Standard Brita Classic</strong>
        loses, it is not certified for microplastics and uses granulated carbon
        with a much coarser particle threshold. <strong>Brita Elite</strong> is
        marginally better but still not NSF 401 certified.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The only certification specifically covering microplastics is <strong>NSF/ANSI 401</strong>. Without it, marketing claims are unverified.</>,
          <>RO systems (AquaTru, APEC, Waterdrop, iSpring) achieve 99%+ removal including nanoplastics.</>,
          <>Clearly Filtered and Epic Pure are the top NSF-certified pitcher options for microplastics.</>,
          <>Berkey is NOT NSF certified for microplastics specifically but uses ceramic + Black Berkey filters with sub-micron pore sizes; effectiveness is well-documented in independent third-party testing.</>,
          <>Standard Brita is not certified for microplastics. Brita Elite has improved performance but still lacks the 401 cert.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The certifications that actually matter
      </h2>
      <p>
        Three independent certifications cover microplastic removal:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>NSF/ANSI 401</strong> (&ldquo;Emerging Compounds/Incidental Contaminants&rdquo;) includes microplastics specifically (added 2018).</li>
        <li><strong>NSF/ANSI 53</strong> (&ldquo;Health Effects&rdquo;) covers particle reduction at &gt;1 µm.</li>
        <li><strong>NSF/ANSI 58</strong>, reverse osmosis system certification.</li>
      </ul>
      <p>
        WQA and IAPMO offer equivalent independent certifications. If a filter
        doesn&apos;t carry one of these, the microplastic claim is essentially
        marketing.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Head-to-head: 12 popular filters
      </h2>

      <BlogComparisonTable
        caption="Popular water filters head-to-head for microplastic removal"
        columns={[
          { key: 'product', label: 'Filter' },
          { key: 'type', label: 'Type' },
          { key: 'mp_removal', label: 'Microplastic removal' },
          { key: 'cert', label: 'NSF 401 certified?' },
          { key: 'price', label: 'Cost system + 1yr' },
        ]}
        rows={[
          { product: 'AquaTru Classic', type: 'Countertop RO', mp_removal: '99%+ incl. nano', cert: 'Yes (also 53, 58)', price: '$450 + $150/yr' },
          { product: 'APEC ROES-50', type: 'Under-sink RO', mp_removal: '99%+ incl. nano', cert: 'Yes (NSF 58)', price: '$210 + $80/yr' },
          { product: 'Waterdrop G3P800', type: 'Under-sink tankless RO', mp_removal: '99%+ incl. nano', cert: 'Yes', price: '$520 + $130/yr' },
          { product: 'iSpring RCC7AK', type: 'Under-sink RO + alkaline', mp_removal: '99%+ incl. nano', cert: 'Yes', price: '$240 + $80/yr' },
          { product: 'Big Berkey', type: 'Gravity ceramic + carbon', mp_removal: '99%+ >0.2 µm (independent testing)', cert: 'Not NSF 401 (independent lab-tested)', price: '$340 + $40/yr' },
          { product: 'Clearly Filtered Pitcher', type: 'Carbon block pitcher', mp_removal: '99% NSF 401', cert: 'Yes (NSF 42, 53, 401, 473)', price: '$90 + $80/yr' },
          { product: 'Epic Pure Pitcher', type: 'Carbon block pitcher', mp_removal: '99% NSF 401', cert: 'Yes (NSF 42, 53, 401)', price: '$70 + $60/yr' },
          { product: 'Aquasana Clean Water Machine', type: 'Countertop pitcher', mp_removal: 'NSF 53 certified for particle removal', cert: 'Partial (53, 42, P473)', price: '$130 + $80/yr' },
          { product: 'Multipure Aquaversa', type: 'Counter/under-sink carbon block', mp_removal: 'NSF 53 certified; manufacturer claims include MPs', cert: 'Yes (NSF 42, 53)', price: '$330 + $60/yr' },
          { product: 'LifeStraw Home Pitcher', type: 'Hollow-fibre + carbon pitcher', mp_removal: 'Claims down to 0.2 µm but not NSF 401', cert: 'NSF 42 + 53; no 401 cert specifically', price: '$60 + $50/yr' },
          { product: 'Brita Elite Pitcher', type: 'Carbon pitcher', mp_removal: 'Improved over Classic; not NSF 401', cert: 'NSF 42, 53 only', price: '$35 + $40/yr' },
          { product: 'Brita Classic Pitcher', type: 'Granulated carbon pitcher', mp_removal: 'Not certified for microplastics', cert: 'NSF 42 only (taste)', price: '$25 + $30/yr' },
        ]}
      />
      <p className="text-sm text-gray-500 italic">
        Certifications change; verify current status on the manufacturer&apos;s
        site or NSF&apos;s database before purchase.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Filter media chemistry, what actually catches the particle
      </h2>
      <p>
        Every filter on the comparison table removes microplastics through one
        of four mechanisms. Understanding which one is in your filter tells
        you what it can and can&apos;t catch, and where it&apos;ll fail first.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Reverse-osmosis membrane.</strong> A semi-permeable membrane
          with effective pores of <strong>0.0001 µm (0.1 nanometres)</strong>.
          Water is forced through under pressure, particles and dissolved
          solids are rejected to a waste stream. RO catches everything from
          micrometre-scale particles down through the entire nanoplastic
          range. The same membrane removes lead, PFAS, fluoride, sodium,
          arsenic, and pharmaceutical residues, the only filter category
          that does. Tradeoff: needs plumbing or counter space, wastes
          ~1–3× as much water as it produces, removes beneficial minerals
          (some systems add an alkaline remineralisation stage).
        </li>
        <li>
          <strong>Carbon block.</strong> Solid extruded activated carbon
          with engineered pore size, typically <strong>0.5–1 µm</strong>.
          Catches microplastics in that range and below by size exclusion,
          and chemical migrants (chlorine, BPA, pesticides) by adsorption.
          Does not catch nanoplastics below ~0.5 µm. Used in Clearly
          Filtered, Epic Pure, Multipure, Aquasana Clean Water Machine.
          Tradeoff: doesn&apos;t remove dissolved minerals or PFAS at high
          rates.
        </li>
        <li>
          <strong>Ceramic.</strong> Porcelain or earthenware element with
          pore size <strong>0.2–0.5 µm</strong>, usually impregnated with
          silver to suppress biological growth. Used in Berkey&apos;s Black
          Berkey filters, Doulton, and gravity-fed systems. Catches
          microplastics &gt;0.2 µm by size exclusion. Cleanable surface
          extends lifespan to years rather than months.
        </li>
        <li>
          <strong>Hollow-fibre membrane.</strong> Bundles of micro-tubes with
          <strong> 0.1–0.2 µm</strong> pore size. Used in LifeStraw Home and
          some emergency-prep filters. Catches microplastics, bacteria, and
          protozoa by size exclusion. Doesn&apos;t catch dissolved chemistry
          and is not effective against viruses.
        </li>
      </ul>
      <p>
        Granulated activated carbon, the bed of small carbon granules in
        a standard Brita Classic, refrigerator filter, or whole-house
        pre-filter, has much coarser effective pore size (typically
        ~5–10 µm) and is engineered for taste and chlorine, not for
        particle exclusion. This is why a Brita Classic does not earn an
        NSF 401 microplastic certification while a Clearly Filtered
        pitcher does, even though both look like &ldquo;a pitcher with
        carbon in it&rdquo; from the outside.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Pore size vs particle size, what each filter actually catches
      </h2>

      <BlogComparisonTable
        caption="Filter pore sizes vs the particle sizes you&apos;re trying to catch"
        columns={[
          { key: 'media', label: 'Filter media' },
          { key: 'pore', label: 'Effective pore size' },
          { key: 'micro', label: 'Microplastics (1 µm – 5 mm)' },
          { key: 'nano', label: 'Nanoplastics (under 1 µm)' },
        ]}
        rows={[
          { media: 'Reverse osmosis membrane', pore: '~0.0001 µm', micro: 'Yes: full range', nano: 'Yes: full range' },
          { media: 'Distillation', pore: 'N/A (phase change)', micro: 'Yes', nano: 'Yes (most)' },
          { media: 'Hollow-fibre (0.1 µm)', pore: '~0.1 µm', micro: 'Yes', nano: 'Partial (only above 100 nm)' },
          { media: 'Ceramic (0.2 µm)', pore: '~0.2 µm', micro: 'Yes', nano: 'Partial (only above 200 nm)' },
          { media: 'NSF 401 carbon block', pore: '~0.5 µm', micro: 'Yes: full range', nano: 'No (below 500 nm)' },
          { media: 'Standard NSF 53 carbon block', pore: '~1 µm', micro: 'Mostly (above 1 µm)', nano: 'No' },
          { media: 'Granulated activated carbon (Brita Classic)', pore: '~5–10 µm', micro: 'Partial (largest only)', nano: 'No' },
          { media: 'Refrigerator filter (typical)', pore: '~0.5–10 µm (varies)', micro: 'Partial', nano: 'No' },
        ]}
      />
      <p>
        The 2024 Qian et al. PNAS study found bottled water averaged{' '}
        <strong>240,000 plastic particles per litre, 90% of which were
        nanoplastics below 1 µm</strong>. The practical implication: if
        you care about nanoplastics specifically, and the evidence says
        you should, only reverse osmosis and distillation get them. Every
        other filter gets the micrometre fraction and misses the nano
        fraction.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the filters don&apos;t remove (so you can decide if it matters)
      </h2>
      <p>
        Microplastic removal is one criterion. Buyers usually also want
        PFAS removal, lead removal, fluoride removal, and chlorination
        byproduct (THM) removal. The coverage gaps by filter type:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Reverse osmosis (AquaTru, APEC, Waterdrop, iSpring):</strong> removes everything. PFAS, lead, fluoride, arsenic, pharmaceutical residue, nitrates. The only filter that removes them all.</li>
        <li><strong>NSF 401 carbon block (Clearly Filtered, Epic, Multipure):</strong> removes PFAS (with the right cartridge), lead, chlorine byproducts. Doesn&apos;t remove fluoride or dissolved minerals.</li>
        <li><strong>Ceramic + carbon (Berkey):</strong> Black Berkey filters remove lead and most heavy metals with the optional PF-2 fluoride filter add-on. Independent testing supports PFAS reduction but is not NSF-certified.</li>
        <li><strong>Hollow-fibre (LifeStraw Home):</strong> good on bacteria and particles, weaker on dissolved chemistry. Pair with a carbon stage for full coverage.</li>
        <li><strong>Standard Brita / granulated carbon / refrigerator filter:</strong> chlorine and taste only in most cases. Lead removal only on specific premium variants.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cost per litre, the number that actually matters
      </h2>
      <p>
        A pitcher looks cheaper than an RO system until you do the math
        per litre of actually-filtered water. Estimates assume two people
        drinking ~2 L/day of filtered water (~1,460 L/year):
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Bottled water:</strong> ~$0.50–$1.00 per litre. $730–$1,460/year for the same volume. Worst per-litre option.</li>
        <li><strong>Brita Classic:</strong> ~$0.02 per litre at chlorine-only level (not microplastic-rated).</li>
        <li><strong>Clearly Filtered pitcher:</strong> ~$0.06 per litre (NSF 401). Year-1 ~$170 total (pitcher + 1 cartridge); year-2+ ~$80.</li>
        <li><strong>AquaTru countertop RO:</strong> ~$0.11 per litre year-1 (system + filters); ~$0.07 per litre year-2+.</li>
        <li><strong>APEC under-sink RO:</strong> ~$0.06 per litre year-1; ~$0.04 per litre year-2+.</li>
        <li><strong>Big Berkey:</strong> ~$0.03 per litre across the system&apos;s expected ~5–10 year lifespan.</li>
      </ul>
      <p>
        Over 5 years, an under-sink RO comes in at roughly the same
        per-litre cost as a Brita Classic but with NSF 58 certification
        including nanoplastic-range removal. The cost argument for
        Brita-class filters disappears the moment you compare per-litre
        properly.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Installation and maintenance, by filter type
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Pitcher (Clearly Filtered, Epic, Brita):</strong> zero
          install. Refill manually. Replace cartridges every 4 months
          (Clearly Filtered) to 2 months (Brita). The most common
          maintenance failure is forgetting to change the cartridge, performance drops sharply past the rated capacity.
        </li>
        <li>
          <strong>Countertop RO (AquaTru):</strong> plug in, fill the
          tank, press a button. Replace pre-filter every 6 months, main
          carbon every 12, RO membrane every 24. Filter-replacement
          subscription is the easiest path.
        </li>
        <li>
          <strong>Under-sink RO (APEC, Waterdrop, iSpring):</strong> 2–4
          hour install (DIY-friendly if your sink has space; otherwise
          $150–$300 plumber). Pre-filter every 6 months, carbon every 12,
          RO membrane every 24, post-filter every 12.
        </li>
        <li>
          <strong>Gravity ceramic (Berkey):</strong> assemble at first
          use. Scrub the white ceramic filters with a Scotch-Brite pad
          every few months when flow rate slows. Black Berkey filters
          last ~6,000 gallons per pair (years of household use).
        </li>
        <li>
          <strong>Whole-house cartridge:</strong> not a microplastic
          solution by default, most whole-house systems run sediment
          and chlorine filters, not microplastic-rated stages. If you
          want point-of-use microplastic protection AND
          whole-house plumbing protection, layer an under-sink RO at the
          kitchen tap on top of whatever whole-house system you have.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about just boiling it?
      </h2>
      <p>
        Worth knowing before you spend anything, because in the right water it
        is free and surprisingly effective. A 2024 study in{' '}
        <em>Environmental Science &amp; Technology Letters</em> found that
        boiling tap water for five minutes and then filtering it once cool
        removes a large share of nano- and microplastics. The mechanism is
        incidental: as the water boils, calcium carbonate comes out of solution
        as limescale and the plastic particles{' '}
        <strong>co-precipitate with it</strong>, so straining out the chalky
        residue takes the particles with it.
      </p>
      <p>
        The catch is that it depends entirely on how hard your water is, and
        the gradient is steep:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>300 mg/L calcium carbonate</strong> (hard water) &mdash; about <strong>90%</strong> removed.</li>
        <li><strong>180 mg/L</strong> &mdash; about <strong>84%</strong>.</li>
        <li><strong>80 mg/L</strong> &mdash; about <strong>34%</strong>.</li>
        <li><strong>Under 60 mg/L</strong> (soft water) &mdash; roughly <strong>25%</strong>, so barely worth the effort.</li>
      </ul>
      <p>
        So this is a genuine free backstop in a hard-water region and close to
        useless in a soft-water one. It also does nothing for dissolved
        chemicals, and boiling every litre you drink is not practical at scale.
        Treat it as a bonus on top of a filter, not a replacement for one.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Best filter by buyer profile
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">If you rent or can&apos;t plumb in</h3>
      <p>
        <strong>AquaTru Classic</strong> ($450). Countertop RO; needs only a
        wall outlet; full NSF 401, 53, and 58 certifications. Removes 99%+ of
        microplastics including nanoplastics. The most upgradeable filter from
        any pitcher.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">If you own and want the best whole-kitchen solution</h3>
      <p>
        <strong>APEC ROES-50</strong> ($210). Under-sink 5-stage RO; installs in
        an afternoon; replaceable filters cost ~$80/year. Best long-term value
        for full microplastic and PFAS removal.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">If you want simple, gravity-fed, off-grid capable</h3>
      <p>
        <strong>Big Berkey</strong> ($340). Stainless steel + ceramic + Black
        Berkey filters; no electricity required; lasts years; widely used and
        independently tested. Not NSF 401 certified (Berkey has chosen not to
        pursue certification) but third-party lab data is strong.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">If you&apos;re budget-constrained (best ROI)</h3>
      <p>
        <strong>Clearly Filtered Pitcher</strong> ($90). NSF 401 certified for
        microplastics; 380 contaminants reduced; works with no installation.
        For under $100 this is the most defensible budget choice.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">If you only care about chlorine/taste</h3>
      <p>
        A basic Brita Classic does the job, but please note it is <em>not</em>
        a microplastic filter despite often being assumed to be.
      </p>

      <p>
        See:{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best water filter for microplastics (buying guide)
        </Link>,{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to filter microplastics from water
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-tap-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in tap water
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Bottled drink material. PET, HDPE, glass, aluminum.</>,
          <>Container condition signals from the photo.</>,
          <>Brand and SKU variant.</>,
          <>Use-context flags you log, heat, reuse, storage time.</>,
          <>Linked published research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan bottled drinks and water products before you buy"
        sub="Tap the barcode and snap a photo. The MicroPlastics app weighs packaging material + brand + condition and gives a 0–100 risk score with a safer swap."
        buttonLabel="Scan water products in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Brita remove microplastics?',
            answer:
              'Standard Brita Classic pitchers are NOT certified for microplastic removal. They use granulated activated carbon designed for taste, chlorine, and a small list of contaminants. Brita Elite is somewhat improved but still does not carry NSF/ANSI 401 microplastic certification.',
          },
          {
            question: 'Does Berkey remove microplastics?',
            answer:
              'Berkey has not pursued NSF/ANSI 401 certification specifically for microplastics, but independent third-party lab testing of their Black Berkey filters has demonstrated greater than 99% removal of particles down to ~0.2 µm. The ceramic + carbon combination is well-regarded for off-grid use.',
          },
          {
            question: 'Is AquaTru worth the price?',
            answer:
              'For dedicated microplastic removal without plumbing, AquaTru Classic is the strongest countertop option. It carries NSF/ANSI 42, 53, 58, 401, and P473 certifications and achieves 99%+ removal including nanoplastics. Annual filter cost is ~$150.',
          },
          {
            question: 'Is reverse osmosis the only way to remove nanoplastics?',
            answer:
              'RO is the most reliable method. Distillation also removes nanoplastics. Carbon block and ceramic filters remove most microplastics down to about 0.2-2 µm depending on the model, but coverage of sub-micron nanoplastics is partial at best.',
          },
          {
            question: 'How much does a good microplastic water filter cost per year?',
            answer:
              'NSF 401 pitchers cost $60-80/year in cartridges after a $70-90 starter. Under-sink RO costs $80-150/year after a $200-500 system. Countertop RO (AquaTru) costs $150/year after a $450 system. Berkey costs $40/year after a $340 starter. All are dramatically cheaper than bottled water per gallon.',
          },
          {
            question: 'Do refrigerator water filters remove microplastics?',
            answer:
              'Most refrigerator filters are NOT NSF 401 certified for microplastics. Some newer Frigidaire and LG filters claim 401 certification, check your specific model number on the NSF database. If yours is not certified for 401, add a pitcher or under-sink filter.',
          },
          {
            question: 'Does boiling water remove microplastics?',
            answer:
              'Yes, to a meaningful degree, especially in hard water. Yu et al. (2024) in Environmental Science & Technology Letters found that boiling hard tap water removes up to 90% of nano- and microplastics by co-precipitation with calcium carbonate scale. The effect is much weaker in soft water. Boiling is a useful complement to filtration but not a replacement for an NSF-certified filter.',
          },
          {
            question: 'Are pitcher filters as effective as under-sink filters for microplastics?',
            answer:
              'NSF 401 pitcher filters (Clearly Filtered, Epic Pure) achieve micrometre-scale microplastic removal comparable to under-sink carbon block systems. Neither category catches nanoplastics below ~500 nm. For nanoplastic-range removal, only reverse osmosis (countertop or under-sink) and distillation work.',
          },
          {
            question: 'Which filter removes both microplastics and PFAS?',
            answer:
              'Reverse osmosis is the most reliable for both. NSF 58 certification covers PFAS reduction and RO catches the full microplastic and nanoplastic range. AquaTru, APEC, Waterdrop, and iSpring RO systems all qualify. NSF 401 + 53 carbon block filters (Clearly Filtered, Epic) also reduce PFAS but at lower documented rates.',
          },
          {
            question: 'How often should I replace my microplastic water filter?',
            answer:
              'Follow the manufacturer&apos;s rated capacity. Typical intervals: Clearly Filtered pitcher every 4 months / 100 gallons; Epic Pure every 3 months; AquaTru pre-filter every 6 months, carbon every 12, RO membrane every 24; under-sink RO similar to AquaTru; Berkey Black filters ~6,000 gallons per pair. Performance drops sharply past the rated capacity, the filter doesn&apos;t fail safely, it stops working.',
          },
          {
            question: 'Is a whole-house water filter enough for microplastics?',
            answer:
              'Usually not. Most whole-house systems run sediment and chlorine filters, not microplastic-rated stages. For point-of-use microplastic protection, layer an under-sink RO or NSF 401 pitcher at the kitchen tap on top of whatever whole-house system you have.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'NSF International',
            year: 2024,
            title: 'NSF/ANSI 401 - Emerging Compounds/Incidental Contaminants (including microplastics)',
            publisher: 'NSF',
            url: 'https://www.nsf.org/standards-development/standards-portfolio/standards-water-treatment-distribution-plumbing',
          },
          {
            authors: 'World Health Organization',
            year: 2019,
            title: 'Microplastics in drinking-water',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789241516198',
          },
          {
            authors: 'US Environmental Protection Agency',
            year: 2024,
            title: 'Drinking Water Treatment Technologies (filter performance and selection)',
            publisher: 'EPA',
            url: 'https://www.epa.gov/sdwa/drinking-water-treatment-technologies',
          },
          {
            authors: 'Yu Z, Wang J, Liu L, et al.',
            year: 2024,
            title: 'Drinking boiled tap water reduces human intake of nanoplastics and microplastics',
            publisher: 'Environmental Science & Technology Letters',
            url: 'https://pubs.acs.org/doi/10.1021/acs.estlett.4c00081',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'Proceedings of the National Academy of Sciences (PNAS)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'World Health Organization',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles and potential implications for human health',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
